// @ts-nocheck
import {Column} from 'react-table'
import {useMemo} from 'react'
import {TourActionsCell} from './TourActionsCell'
import {TourCustomHeader} from './TourCustomHeader'
import {Tour} from '../../core/_models'
import {TourInfoCell} from './TourInfoCell'
import {TourLastLoginCell} from './TourLastLoginCell'
import {TourTwoStepsCell} from './TourTwoStepsCell'
import {TourJoinedDay} from './TourJoinedDay'
import {TourSelectionCell} from './TourSelectionCell'
import {TourSelectionHeader} from './TourSelectionHeader'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useLang} from '../../../../../../_dashboard/i18n/Metronici18n'

// Function to extract timestamp from MongoDB ObjectID
const getDateFromObjectId = (objectId: string): Date | null => {
  if (!objectId) return null;
  
  try {
    // MongoDB ObjectIDs are 24 characters hexadecimal strings
    if (typeof objectId !== 'string' || objectId.length !== 24) {
      return null;
    }
    
    // Extract timestamp (first 4 bytes - 8 characters)
    const timestamp = parseInt(objectId.substring(0, 8), 16) * 1000;
    
    // Convert to milliseconds and create Date object
    return new Date(timestamp);
  } catch (error) {
    return null;
  }
};

/** Titre du tour dans la langue admin (fallback fr, puis racine). */
const resolveTourListTitle = (tour: Tour | undefined, lang: string): string => {
  if (!tour) return ''
  const translations = Array.isArray(tour.translations) ? tour.translations : []
  const byLang = (code: string) =>
    translations.find((t) => String(t?.language || '').toLowerCase() === code)?.title?.trim()

  const code = String(lang || 'fr').toLowerCase()
  return (
    byLang(code) ||
    (typeof tour.title === 'string' ? tour.title.trim() : '') ||
    byLang('fr') ||
    translations.find((t) => t?.title?.trim())?.title?.trim() ||
    ''
  )
}

export const useToursColumns = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const lang = useLang() || 'fr'
  
  const columns = useMemo(() => [
    {
      Header: (props) => <TourCustomHeader tableProps={props} title={intl.formatMessage({id: 'TOUR.FIELDS.TITLE'})} className='min-w-125px' />,
      id: 'name',
      Cell: ({...props}) => {
        const tour = props.data[props.row.index]
        const handleClick = () => {
          navigate(`/apps/tours/${tour._id}`)
        }
        return (
          <span className='text-gray-800 text-hover-primary mb-1 cursor-pointer' onClick={handleClick}>
            {resolveTourListTitle(tour, lang)}
          </span>
        )
      },
    },
    {
      Header: (props) => <TourCustomHeader tableProps={props} title={intl.formatMessage({id: 'TOUR.FIELDS.DURATION'})} className='min-w-125px' />,
      id: 'duration',
      Cell: ({...props}) => {
        const tour = props.data[props.row.index];
        const durationValue = tour.duration;
        if (durationValue == null || durationValue === '') {
          return <span>—</span>;
        }
        const unitLabel =
          tour.durationUnit === 'days'
            ? intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.DAYS'})
            : intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.HOURS'});
        return (
          <span>
            {durationValue} {unitLabel}
          </span>
        );
      },
    },
    {
      Header: (props) => <TourCustomHeader tableProps={props} title={intl.formatMessage({id: 'TOUR.FIELDS.CAPACITY'})} className='min-w-125px' />,
      id: 'capacity',
      Cell: ({...props}) => (
        <span>
          {props.data[props.row.index].tourMinPeople} - {props.data[props.row.index].tourMaxPeople}
        </span>
      ),
    },
    {
      Header: (props) => <TourCustomHeader tableProps={props} title={intl.formatMessage({id: 'TOUR.FIELDS.STATUS'})} className='min-w-125px' />,
      id: 'status',
      Cell: ({...props}) => (
        <span className={`badge badge-light-${props.data[props.row.index].status === 'draft' ? 'warning' : 'success'}`}>
          {props.data[props.row.index].status === 'draft' 
            ? intl.formatMessage({id: 'TOUR.FIELDS.PUBLISHED_STATE_OFF'}) 
            : intl.formatMessage({id: 'TOUR.FIELDS.PUBLISHED_STATE_ON'})}
        </span>
      ),
    },
    {
      Header: (props) => <TourCustomHeader tableProps={props} title={intl.formatMessage({id: 'COLUMNS.CREATED_AT'})} className='min-w-125px' />,
      id: 'createdAt',
      Cell: ({...props}) => {
        const tour = props.data[props.row.index];
        let date: Date | null = null;
        
        // Try to use createdAt field first
        if (tour.createdAt) {
          date = new Date(tour.createdAt);
        } 
        // If no createdAt, extract date from ObjectID
        else {
          date = getDateFromObjectId(tour._id);
        }
        
        // Format date if valid, otherwise show placeholder
        if (date && !isNaN(date.getTime())) {
          return <span>{date.toLocaleDateString('fr-FR')}</span>;
        } else {
          return <span>-</span>;
        }
      },
    },
    {
      Header: (props) => (
        <TourCustomHeader tableProps={props} title='' className='text-end min-w-100px' />
      ),
      id: 'actions',
      Cell: ({...props}) => {
        const tour = props.data[props.row.index]
        const tourTitle = resolveTourListTitle(tour, lang)
        return <TourActionsCell id={tour._id} tourTitle={tourTitle} />
      },
    }
  ], [intl, navigate, lang])

  return columns
}
